import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Plus, ArrowLeft, Send, Image as ImageIcon, X, Users, Search, Check, MoreVertical, Pencil, Trash2, CornerUpLeft, Reply } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth, formatError } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function initials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
}

function fileUrl(p) {
  if (!p) return '';
  return p.startsWith('http') ? p : `${API}/files/${p}`;
}

function timeHM(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function dayLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate()-1);
  if (d.toDateString() === today.toDateString()) return 'Bugün';
  if (d.toDateString() === yest.toDateString()) return 'Dün';
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' });
}

export default function MessagesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { convId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null); // {path, type, preview}
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // {_id, sender_name, text}
  const textInputRef = useRef(null);
  const [showNew, setShowNew] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/giris'); return; }
    fetchConvs();
    const t = setInterval(fetchConvs, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  useEffect(() => {
    if (!convId) { setActive(null); setMessages([]); return; }
    loadConversation(convId);
    const t = setInterval(() => loadMessages(convId, true), 7000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  const fetchConvs = async () => {
    try {
      const r = await axios.get(`${API}/messages/conversations`, { withCredentials: true });
      setConversations(r.data.conversations || []);
    } catch { /* ignore */ }
    finally { setLoadingList(false); }
  };

  const loadConversation = async (id) => {
    setLoadingMsgs(true);
    try {
      const [c, m] = await Promise.all([
        axios.get(`${API}/messages/conversations/${id}`, { withCredentials: true }),
        axios.get(`${API}/messages/conversations/${id}/messages?limit=100`, { withCredentials: true }),
      ]);
      setActive(c.data.conversation);
      setMessages(m.data.messages || []);
      await axios.post(`${API}/messages/conversations/${id}/read`, {}, { withCredentials: true }).catch(()=>{});
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 404) {
        toast.error('Sohbet bulunamadı');
        navigate('/mesajlar');
      }
    } finally { setLoadingMsgs(false); }
  };

  const loadMessages = async (id, silent=false) => {
    try {
      const r = await axios.get(`${API}/messages/conversations/${id}/messages?limit=100`, { withCredentials: true });
      const next = r.data.messages || [];
      if (!silent || next.length !== messages.length) {
        setMessages(next);
        if (next.length) {
          axios.post(`${API}/messages/conversations/${id}/read`, {}, { withCredentials: true }).catch(()=>{});
          setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
        }
      }
    } catch { /* ignore */ }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Dosya 10MB\'dan büyük'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/messages/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachment({ path: r.data.path, type: 'image', preview: URL.createObjectURL(file) });
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (sending) return;
    const t = text.trim();
    if (!t && !attachment) return;
    setSending(true);
    try {
      const payload = {
        text: t,
        attachments: attachment ? [{ path: attachment.path, type: attachment.type }] : [],
      };
      if (replyTo) payload.reply_to = replyTo._id;
      const r = await axios.post(`${API}/messages/conversations/${active._id}/messages`, payload, { withCredentials: true });
      setMessages(prev => [...prev, r.data.message]);
      setText(''); setAttachment(null); setReplyTo(null);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 50);
      fetchConvs();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSending(false); }
  };

  const handleEdit = async (msgId, newText) => {
    try {
      const r = await axios.patch(`${API}/messages/${msgId}`, { text: newText }, { withCredentials: true });
      setMessages(prev => prev.map(m => m._id === msgId ? { ...m, ...r.data.message } : m));
      fetchConvs();
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleDelete = async (msgId) => {
    if (!window.confirm('Bu mesajı silmek istiyor musunuz?')) return;
    try {
      await axios.delete(`${API}/messages/${msgId}`, { withCredentials: true });
      setMessages(prev => prev.map(m => m._id === msgId ? { ...m, deleted: true, text: '', attachments: [] } : m));
      fetchConvs();
      toast.success('Mesaj silindi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const startReply = (m) => {
    setReplyTo({ _id: m._id, sender_name: m.sender_name, text: m.text || (m.attachments?.length ? '📷 Fotoğraf' : '') });
    setTimeout(() => textInputRef.current?.focus?.(), 50);
  };

  const handleDeleteConversation = async () => {
    if (!active) return;
    if (!window.confirm('Bu sohbeti tüm mesajlarıyla birlikte silmek istiyor musunuz? Bu işlem geri alınamaz ve karşı tarafın sohbeti de silinir.')) return;
    try {
      await axios.delete(`${API}/messages/conversations/${active._id}`, { withCredentials: true });
      setConversations(prev => prev.filter(c => c._id !== active._id));
      toast.success('Sohbet silindi');
      navigate('/mesajlar');
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const openConv = (id) => { navigate(`/mesajlar/${id}`); };

  if (!user) return null;

  // Mobile: either list or detail
  const detailOpen = !!convId;

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6" data-testid="messages-page">
      <SEO title="Mesajlar · MiniOto" description="Üyeler arası özel ve grup mesajlaşma" />
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden flex" style={{ minHeight: 'calc(100vh - 180px)' }}>
        {/* LIST */}
        <aside className={`w-full md:w-80 lg:w-96 border-r border-slate-100 flex-col ${detailOpen ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-3 border-b border-slate-100 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
              <h2 className="font-bold text-slate-800">Mesajlar</h2>
            </div>
            <Button size="sm" onClick={() => setShowNew(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs" data-testid="messages-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Yeni
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingList ? (
              <div className="p-6 text-center text-slate-400 text-sm">Yükleniyor...</div>
            ) : conversations.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">
                Henüz sohbetiniz yok.<br/>
                <button onClick={() => setShowNew(true)} className="text-emerald-600 font-semibold mt-2">Yeni sohbet başlat</button>
              </div>
            ) : conversations.map(c => {
              const isActive = convId === c._id;
              const avatar = c.type === 'group' ? '' : (c.participants.find(p => p.user_id !== user._id)?.avatar_url || '');
              return (
                <button
                  key={c._id}
                  onClick={() => openConv(c._id)}
                  data-testid={`conv-${c._id}`}
                  className={`w-full text-left px-3 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex items-center gap-3 ${isActive ? 'bg-emerald-50' : ''}`}
                >
                  <Avatar className="w-11 h-11 shrink-0">
                    <AvatarImage src={fileUrl(avatar)} />
                    <AvatarFallback className={c.type === 'group' ? 'bg-violet-100 text-violet-700' : 'bg-emerald-100 text-emerald-700'}>
                      {c.type === 'group' ? <Users className="w-5 h-5" /> : initials(c.display_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm text-slate-800 truncate">{c.display_name}</span>
                      <span className="text-[10px] text-slate-400 shrink-0">{timeHM(c.last_message_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-500 truncate">{c.last_message?.text || 'Sohbet başlatıldı'}</span>
                      {c.unread > 0 && (
                        <span className="bg-emerald-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center shrink-0" data-testid={`conv-unread-${c._id}`}>
                          {c.unread}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* DETAIL */}
        <section className={`flex-1 flex-col ${detailOpen ? 'flex' : 'hidden md:flex'}`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-6">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                Sohbet seçin veya yeni başlatın
              </div>
            </div>
          ) : (
            <>
              <header className="px-3 sm:px-4 py-3 border-b border-slate-100 flex items-center gap-3">
                <button onClick={() => navigate('/mesajlar')} className="md:hidden p-1.5 rounded-lg hover:bg-slate-100" data-testid="messages-back-btn">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <Avatar className="w-9 h-9">
                  {active.type === 'group' ? (
                    <AvatarFallback className="bg-violet-100 text-violet-700"><Users className="w-4 h-4" /></AvatarFallback>
                  ) : (
                    <>
                      <AvatarImage src={fileUrl((active.participants || []).find(p => p.user_id !== user._id)?.avatar_url)} />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700">{initials(active.name || (active.participants?.find(p => p.user_id !== user._id)?.display_name) || 'U')}</AvatarFallback>
                    </>
                  )}
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-slate-800 truncate">
                    {active.name || (active.participants || []).find(p => p.user_id !== user._id)?.display_name || 'Sohbet'}
                  </div>
                  <div className="text-xs text-slate-400 truncate">
                    {active.type === 'group' ? `${active.participants?.length} kişi` : (active.participants || []).find(p => p.user_id !== user._id)?.username ? `@${(active.participants || []).find(p => p.user_id !== user._id).username}` : ''}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0" data-testid="conv-menu-btn" aria-label="Sohbet menüsü">
                      <MoreVertical className="w-5 h-5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={handleDeleteConversation} className="text-red-600" data-testid="conv-delete-btn">
                      <Trash2 className="w-3.5 h-3.5 mr-2" /> Sohbeti Sil
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </header>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 space-y-2 bg-slate-50" data-testid="messages-stream">
                {loadingMsgs && messages.length === 0 ? (
                  <div className="text-center text-slate-400 text-sm py-8">Yükleniyor...</div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-slate-400 text-sm py-8">İlk mesajı sen gönder 👋</div>
                ) : (
                  (() => {
                    let lastDay = '';
                    return messages.map(m => {
                      const mine = m.sender_id === user._id;
                      const d = dayLabel(m.created_at);
                      const showDay = d !== lastDay;
                      lastDay = d;
                      return (
                        <React.Fragment key={m._id}>
                          {showDay && (
                            <div className="flex justify-center my-2">
                              <span className="text-[10px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">{d}</span>
                            </div>
                          )}
                          <MessageBubble
                            msg={m}
                            mine={mine}
                            isGroup={active.type === 'group'}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onReply={startReply}
                          />
                        </React.Fragment>
                      );
                    });
                  })()
                )}
              </div>

              <form onSubmit={handleSend} className="border-t border-slate-100 p-2 sm:p-3 bg-white">
                {replyTo && (
                  <div className="mb-2 flex items-start gap-2 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg px-3 py-2" data-testid="reply-preview">
                    <CornerUpLeft className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-semibold text-emerald-700">{replyTo.sender_name || 'Mesaj'}</div>
                      <div className="text-xs text-slate-600 truncate">{replyTo.text || '📷 Fotoğraf'}</div>
                    </div>
                    <button type="button" onClick={() => setReplyTo(null)} className="text-slate-400 hover:text-red-500 shrink-0" data-testid="reply-cancel">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {attachment && (
                  <div className="relative inline-block mb-2">
                    <img src={attachment.preview} alt="" className="h-20 rounded-lg object-cover" />
                    <button type="button" onClick={() => setAttachment(null)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5" data-testid="msg-remove-attachment">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="msg-attach-btn" title="Fotoğraf ekle">
                    <ImageIcon className="w-5 h-5" />
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="msg-file-input" />
                  <Input
                    ref={textInputRef}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder="Mesaj yaz..."
                    className="flex-1 h-10"
                    data-testid="msg-text-input"
                  />
                  <Button type="submit" disabled={sending || uploading || (!text.trim() && !attachment)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-3" data-testid="msg-send-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </>
          )}
        </section>
      </div>

      <NewConversationDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(c) => { setShowNew(false); fetchConvs(); navigate(`/mesajlar/${c._id}`); }}
        me={user}
      />
    </div>
  );
}

function MessageBubble({ msg, mine, isGroup, onEdit, onDelete, onReply }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.text || '');

  useEffect(() => { setDraft(msg.text || ''); }, [msg.text]);

  const save = async () => {
    const t = draft.trim();
    if (!t) return;
    if (t === (msg.text || '')) { setEditing(false); return; }
    await onEdit(msg._id, t);
    setEditing(false);
  };

  if (msg.deleted) {
    return (
      <div className={`flex ${mine ? 'justify-end' : 'justify-start'} items-end gap-2`}>
        {!mine && (
          <Avatar className="w-7 h-7 shrink-0">
            <AvatarImage src={fileUrl(msg.sender_avatar)} />
            <AvatarFallback className="bg-emerald-100 text-emerald-700 text-[10px]">{initials(msg.sender_name)}</AvatarFallback>
          </Avatar>
        )}
        <div className={`max-w-[75%] rounded-2xl px-3 py-2 italic text-sm ${mine ? 'bg-emerald-100 text-emerald-700 rounded-br-sm' : 'bg-slate-100 text-slate-400 rounded-bl-sm'}`} data-testid={`msg-${msg._id}`}>
          Bu mesaj silindi
          <div className="text-[10px] mt-0.5 text-right opacity-70">{timeHM(msg.created_at)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'} items-end gap-2 group`}>
      {!mine && (
        <Avatar className="w-7 h-7 shrink-0">
          <AvatarImage src={fileUrl(msg.sender_avatar)} />
          <AvatarFallback className="bg-emerald-100 text-emerald-700 text-[10px]">{initials(msg.sender_name)}</AvatarFallback>
        </Avatar>
      )}
      <div className="flex items-end gap-1 max-w-[85%]">
        {mine && !editing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded-full text-slate-400 hover:bg-slate-200 opacity-60 group-hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-testid={`msg-menu-${msg._id}`} aria-label="Mesaj menüsü">
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuItem onClick={() => onReply && onReply(msg)} data-testid={`msg-reply-${msg._id}`}>
                <Reply className="w-3.5 h-3.5 mr-2" /> Yanıtla
              </DropdownMenuItem>
              {msg.text && (
                <DropdownMenuItem onClick={() => setEditing(true)} data-testid={`msg-edit-${msg._id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-2" /> Düzenle
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onDelete(msg._id)} className="text-red-600" data-testid={`msg-delete-${msg._id}`}>
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Sil
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <div className={`rounded-2xl px-3 py-2 ${mine ? 'bg-emerald-500 text-white rounded-br-sm' : 'bg-white border border-slate-100 text-slate-800 rounded-bl-sm'}`} data-testid={`msg-${msg._id}`}>
          {!mine && isGroup && (
            <div className="text-[10px] font-semibold text-emerald-700 mb-0.5">{msg.sender_name}</div>
          )}
          {msg.reply_to && (
            <div className={`mb-1 rounded-lg px-2 py-1 border-l-2 text-[11px] ${mine ? 'bg-white/20 border-white/60 text-emerald-50' : 'bg-slate-50 border-emerald-400 text-slate-500'}`} data-testid={`msg-replyref-${msg._id}`}>
              <div className={`font-semibold ${mine ? 'text-white' : 'text-emerald-700'}`}>{msg.reply_to.sender_name || 'Mesaj'}</div>
              <div className="truncate">{msg.reply_to.text || '📷 Fotoğraf'}</div>
            </div>
          )}
          {msg.attachments?.map((a, i) => a.type === 'image' && (
            <a key={i} href={fileUrl(a.path)} target="_blank" rel="noopener noreferrer" className="block mb-1">
              <img src={fileUrl(a.path)} alt="" className="rounded-lg max-h-60 max-w-full object-cover" />
            </a>
          ))}
          {editing ? (
            <div className="space-y-1 min-w-[200px]">
              <textarea
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); } if (e.key === 'Escape') setEditing(false); }}
                className="w-full bg-white/90 text-slate-800 text-sm rounded px-2 py-1 outline-none resize-none"
                rows={2}
                autoFocus
                data-testid={`msg-edit-input-${msg._id}`}
              />
              <div className="flex items-center justify-end gap-1 text-[11px]">
                <button type="button" onClick={() => setEditing(false)} className="px-2 py-0.5 rounded bg-white/20 hover:bg-white/30">İptal</button>
                <button type="button" onClick={save} className="px-2 py-0.5 rounded bg-white text-emerald-700 font-semibold" data-testid={`msg-edit-save-${msg._id}`}>Kaydet</button>
              </div>
            </div>
          ) : (
            msg.text && <div className="text-sm whitespace-pre-wrap break-words">{msg.text}</div>
          )}
          <div className={`text-[10px] mt-0.5 ${mine ? 'text-emerald-100' : 'text-slate-400'} text-right flex items-center justify-end gap-1`}>
            {msg.edited_at && <span className="opacity-80">· düzenlendi</span>}
            {timeHM(msg.created_at)}
          </div>
        </div>
        {!mine && !editing && (
          <button type="button" onClick={() => onReply && onReply(msg)} className="p-1 rounded-full text-slate-400 hover:bg-slate-200 opacity-60 group-hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-testid={`msg-reply-btn-${msg._id}`} aria-label="Yanıtla" title="Yanıtla">
            <Reply className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function NewConversationDialog({ open, onClose, onCreated, me }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState([]);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked([]); setName(''); setSearch('');
    axios.get(`${API}/users?limit=200&sort=recent`).then(r => setUsers((r.data.users || []).filter(u => u._id !== me?._id))).catch(()=>{});
  }, [open, me]);

  const togglePick = (u) => {
    setPicked(prev => prev.find(p => p._id === u._id) ? prev.filter(p => p._id !== u._id) : [...prev, u]);
  };

  const filtered = users.filter(u => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.username || '').toLowerCase().includes(q) || (u.display_name || '').toLowerCase().includes(q);
  });

  const submit = async () => {
    if (picked.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        participant_ids: picked.map(p => p._id),
        name: picked.length > 1 ? (name.trim() || 'Grup') : '',
        type: picked.length > 1 ? 'group' : 'dm',
      };
      const r = await axios.post(`${API}/messages/conversations`, payload, { withCredentials: true });
      onCreated(r.data.conversation);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="new-conv-dialog">
        <DialogHeader><DialogTitle>Yeni Sohbet</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {picked.length > 1 && (
            <Input placeholder="Grup adı (isteğe bağlı)" value={name} onChange={e => setName(e.target.value)} data-testid="new-conv-name" />
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Üye ara..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="new-conv-search" />
          </div>
          {picked.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {picked.map(p => (
                <button key={p._id} onClick={() => togglePick(p)} className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1">
                  {p.display_name || p.username} <X className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}
          <div className="max-h-60 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-50">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400">Kullanıcı bulunamadı</div>
            ) : filtered.map(u => {
              const isPicked = picked.find(p => p._id === u._id);
              return (
                <button key={u._id} onClick={() => togglePick(u)} className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-50" data-testid={`new-conv-user-${u._id}`}>
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={fileUrl(u.avatar_url)} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">{initials(u.display_name || u.username)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">{u.display_name || u.username}</div>
                    <div className="text-xs text-slate-400 truncate">@{u.username}</div>
                  </div>
                  {isPicked && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>İptal</Button>
            <Button onClick={submit} disabled={picked.length === 0 || loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-conv-create">
              {picked.length > 1 ? 'Grubu Oluştur' : 'Sohbeti Başlat'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
